import type { PushState } from './push-state'

/**
 * Whether to ask somebody to turn push on, and which ask (issue #351,
 * docs/PUSH_OPT_IN_PLAN.md). Every nudge — the card, the dialog after a follow
 * or a send — goes through this one function rather than working it out again.
 *
 * - `loading` — not known yet: nothing is drawn, so a member push already
 *   reaches never sees an ask flash and vanish;
 * - `none` — nothing to ask;
 * - `install` — iOS or iPadOS outside the installed app, where only installing
 *   brings notifications;
 * - `blocked` — the person said no in this browser's prompt, which the page can
 *   never ask again; only the steps to undo it are left;
 * - `ask` — the browser can still ask.
 */
export type PushNudge = 'loading' | 'none' | 'install' | 'blocked' | 'ask'

interface NudgeUser {
  id: number | string
  push_devices_count?: number
}

export function pushNudgeState(user: NudgeUser | null | undefined, push: PushState): PushNudge {
  if (!user) return 'none'
  // The sign-in response carries no owner fields: the count arrives with the
  // profile, a moment later.
  if (push.loading || user.push_devices_count === undefined) return 'loading'
  // Reachable anywhere is reachable: no ask on a second device.
  if (user.push_devices_count > 0) return 'none'

  const { registration, endpoint } = push
  if (endpoint && registration?.userId === String(user.id) && registration.endpoint === endpoint && registration.registered) {
    return 'none'
  }

  // Before `permission`, which on iOS outside the app is a stand-in "denied".
  if (push.needsAppInstall) return 'install'
  if (!push.isSupported) return 'none'
  if (push.permission === 'denied') return 'blocked'
  return 'ask'
}
